import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Loader,
  Text,
  Stack,
  Badge,
  Group,
  Pagination,
  Select,
  useMantineTheme,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { useMediaQuery } from '@mantine/hooks';
import { IconArrowUp, IconArrowDown, IconArrowsSort } from '@tabler/icons-react';
import { Content } from '@services/content-service';
import styles from './ContentTable.module.scss';
import { ContentTableControls } from './ContentTableControls';
import { ContentTableBody } from './ContentTableBody';
import { useUnpublishContent } from '@hooks/useUnpublishContent';
import { useDeleteContent } from '@hooks/useDeleteContent';
import { useGetAllContents } from '@hooks/useGetAllContents';

interface ContentTableProps {
  className?: string;
}

export const ContentTable = ({ className }: ContentTableProps) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
  const isTablet = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);

  const { contents, isLoading, error, errorDetails, refreshContents } = useGetAllContents();
  const { unpublishContent } = useUnpublishContent();
  const { deleteContent } = useDeleteContent();

  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState<keyof Content>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [langFilter, setLangFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const handleSort = (column: keyof Content) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('desc');
    }
  };

  const renderSortIcon = (column: keyof Content) => {
    if (sortBy !== column) return <IconArrowsSort size={16} />;
    return sortDirection === 'asc' ? <IconArrowUp size={16} /> : <IconArrowDown size={16} />;
  };

  const filteredContents = contents.filter(content => {
    const matchesSearch = content.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || content.status === statusFilter;
    const matchesLang = langFilter === 'all' || content.lang === langFilter;
    return matchesSearch && matchesStatus && matchesLang;
  });

  const sortedContents = [...filteredContents].sort((a, b) => {
    const aValue = a[sortBy] || '';
    const bValue = b[sortBy] || '';
    if (aValue === bValue) return 0;
    return sortDirection === 'asc' ? (aValue < bValue ? -1 : 1) : aValue > bValue ? -1 : 1;
  });

  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, sortedContents.length);
  const paginatedContents = sortedContents.slice(startIndex, endIndex);
  const totalPages = Math.ceil(sortedContents.length / itemsPerPage);

  const formatDate = (date: string) => new Date(date).toLocaleDateString();

  const renderStatus = (status: string) => {
    let color = 'gray';
    let text = status;

    switch (status) {
      case 'P':
        color = 'green';
        text = t('newContent.status.published');
        break;
      case 'U':
        color = 'orange';
        text = t('newContent.status.unpublished');
        break;
      case 'D':
        color = 'red';
        text = t('newContent.status.deleted');
        break;
    }

    return <Badge color={color}>{text}</Badge>;
  };

  const handlePublishToggle = async (content: Content) => {
    if (!content?._id) return;
    try {
      await unpublishContent(content._id);
      refreshContents();
    } catch (error) {
      console.error('Error toggling publish status:', error);
    }
  };

  const handleDelete = async (content: Content) => {
    if (!content?._id) return;
    modals.openConfirmModal({
      title: t('contentTable.confirmDeleteTitle'),
      centered: true,
      children: <Text size="sm">{t('contentTable.confirmDelete')}</Text>,
      labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteContent(content._id);
        } catch (error) {
          console.error('Error deleting content:', error);
        }
      },
    });
  };

  if (isLoading) {
    return (
      <Box className={styles['contentTable__loading']}>
        <Loader size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={styles['contentTable__error']}>
        <Text>{error}</Text>
        {errorDetails && (
          <Text size="sm" c="red.7" mt="xs">
            {errorDetails}
          </Text>
        )}
      </Box>
    );
  }

  return (
    <Box className={`${styles.contentTable} ${className || ''}`}>
      <Stack gap="md">
        <ContentTableControls
          contents={contents}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          langFilter={langFilter}
          setLangFilter={setLangFilter}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
        />

        <ContentTableBody
          contents={paginatedContents}
          handleSort={handleSort}
          renderSortIcon={renderSortIcon}
          formatDate={formatDate}
          renderStatus={renderStatus}
          handlePublishToggle={handlePublishToggle}
          handleDelete={handleDelete}
        />

        {paginatedContents.length > 0 && (
          <Group
            justify="space-between"
            mt="md"
            wrap={isMobile || isTablet ? 'wrap' : 'nowrap'}
            className={styles['contentTable__pagination']}
          >
            <Group gap="xs" className={styles['contentTable__pagination-info']}>
              <Text size="sm" c="dimmed">
                {t('common.showing')} {startIndex + 1}-{endIndex} {t('common.of')}{' '}
                {sortedContents.length}
              </Text>
              <Select
                value={String(itemsPerPage)}
                onChange={value => {
                  setItemsPerPage(Number(value));
                  setPage(1);
                }}
                data={[
                  { value: '5', label: '5' },
                  { value: '10', label: '10' },
                  { value: '20', label: '20' },
                  { value: '50', label: '50' },
                ]}
                size="xs"
                style={{ width: '80px' }}
              />
            </Group>
            <Pagination
              value={page}
              onChange={setPage}
              total={totalPages}
              size={isMobile ? 'sm' : 'md'}
            />
          </Group>
        )}
      </Stack>
    </Box>
  );
};
